import { useState, type ReactNode } from "react";
import { Avatar } from "@design-system/Avatar/Avatar";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavPickerTrigger } from "@design-system/SideNav/SideNavPickerTrigger";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { SideNavSubItem } from "@design-system/SideNav/SideNavSubItem";
import type { SideNavForcedState } from "@design-system/SideNav/SideNav.types";
import { AccountsIcon, TransactionsIcon, WorkIcon } from "@icons";
import { taskCountTrailing } from "@components/navigation/sideNav/ActionsRow";
import { VariantGrid, VariantItem } from "./playgroundControls";
import {
    PlaygroundAddClientButton,
    PlaygroundTaskSubItems,
} from "./SideNavPlaygroundParts";
import styles from "./SideNavPlayground.module.scss";

const noop = () => {};

/** A strip of nav surface at the real 220px width, so rows sit at their real inset. */
const Plate = ({ children }: { children: ReactNode }) => (
    <div className={styles.plate}>{children}</div>
);

const Row = ({
    label = "Accounts",
    forcedState,
    isActive,
}: {
    label?: string;
    forcedState?: SideNavForcedState;
    isActive?: boolean;
}) => (
    <SideNavItem
        onClick={noop}
        icon={<AccountsIcon />}
        label={label}
        isActive={isActive}
        data-forced-state={forcedState}
    />
);

const ExpandableRow = ({
    isInitiallyExpanded,
    forcedState,
    children = <SideNavSubItem onClick={noop} label="Categories" />,
}: {
    isInitiallyExpanded: boolean;
    forcedState?: SideNavForcedState;
    children?: ReactNode;
}) => {
    const [isExpanded, setIsExpanded] = useState(() => isInitiallyExpanded);
    return (
        <SideNavItem
            onClick={noop}
            icon={<TransactionsIcon />}
            label="Transactions"
            isExpanded={isExpanded}
            onExpandedChange={setIsExpanded}
            data-forced-state={forcedState}
        >
            {children}
        </SideNavItem>
    );
};

const pickerStates: ReadonlyArray<{ label: string; forcedState?: "hover" }> = [
    { label: "rest" },
    { label: "hover", forcedState: "hover" },
];

const Section = ({
    isInitiallyOpen,
    forcedState,
    action,
}: {
    isInitiallyOpen: boolean;
    forcedState?: SideNavForcedState;
    action?: ReactNode;
}) => {
    const [isOpen, setIsOpen] = useState(() => isInitiallyOpen);
    return (
        <SideNavSection
            label="Clients"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            action={action}
            data-forced-state={forcedState}
        >
            <Row label="Northwind Holdings" />
            <Row label="All clients" />
        </SideNavSection>
    );
};

/** Every row, section and trigger state side by side, forced where it needs a pointer. */
export const SideNavStateMatrix = () => (
    <>
        <VariantGrid title="Item states">
            <VariantItem label="rest">
                <Plate>
                    <Row />
                </Plate>
            </VariantItem>
            <VariantItem label="hover">
                <Plate>
                    <Row forcedState="hover" />
                </Plate>
            </VariantItem>
            <VariantItem label="active">
                <Plate>
                    <Row isActive />
                </Plate>
            </VariantItem>
            <VariantItem label="focus">
                <Plate>
                    <Row forcedState="focus" />
                </Plate>
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="Item slots">
            <VariantItem label="trailing Tag, active and rest">
                <Plate>
                    {[true, false].map((isActive) => (
                        <SideNavItem
                            key={String(isActive)}
                            onClick={noop}
                            icon={<WorkIcon />}
                            label="Actions"
                            isActive={isActive}
                            trailing={taskCountTrailing(12)}
                        />
                    ))}
                </Plate>
            </VariantItem>
            <VariantItem label="hoverAction (forced hover)">
                <Plate>
                    <SideNavItem
                        onClick={noop}
                        icon={<AccountsIcon />}
                        label="Accounts"
                        hoverAction={<PlaygroundAddClientButton />}
                        data-forced-state="hover"
                    />
                </Plate>
            </VariantItem>
            <VariantItem label="truncated label (hover it)">
                <Plate>
                    <Row label="Northwind Holdings International Partners LLC" />
                </Plate>
            </VariantItem>
            <VariantItem label="expandable: collapsed (forced hover)">
                <Plate>
                    <ExpandableRow
                        isInitiallyExpanded={false}
                        forcedState="hover"
                    />
                </Plate>
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="Section">
            <VariantItem label="expanded">
                <Plate>
                    <Section isInitiallyOpen />
                </Plate>
            </VariantItem>
            <VariantItem label="collapsed">
                <Plate>
                    <Section isInitiallyOpen={false} />
                </Plate>
            </VariantItem>
            <VariantItem label="collapsed, hover with action">
                <Plate>
                    <Section
                        isInitiallyOpen={false}
                        forcedState="hover"
                        action={<PlaygroundAddClientButton />}
                    />
                </Plate>
            </VariantItem>
            <VariantItem label="focus">
                <Plate>
                    <Section
                        isInitiallyOpen={false}
                        forcedState="focus"
                        action={<PlaygroundAddClientButton />}
                    />
                </Plate>
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="Sub-item">
            <VariantItem label="tree: rest, hover, active">
                <Plate>
                    <ExpandableRow isInitiallyExpanded>
                        <SideNavSubItem onClick={noop} label="P&L" />
                        <SideNavSubItem
                            onClick={noop}
                            label="Balance Sheet"
                            data-forced-state="hover"
                        />
                        <SideNavSubItem
                            onClick={noop}
                            label="All reports"
                            isActive
                        />
                    </ExpandableRow>
                </Plate>
            </VariantItem>
            <VariantItem label="status: unread, inProgress, none">
                <Plate>
                    <PlaygroundTaskSubItems
                        target={() => ({ onClick: noop })}
                    />
                </Plate>
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="Picker trigger">
            {pickerStates.map(({ label, forcedState }) => (
                <VariantItem key={label} label={label}>
                    <Plate>
                        <SideNavPickerTrigger
                            avatar={
                                <Avatar
                                    name="Acme Holdings"
                                    size="sm"
                                    shape="square"
                                />
                            }
                            label="Acme Holdings"
                            data-forced-state={forcedState}
                        />
                    </Plate>
                </VariantItem>
            ))}
        </VariantGrid>
    </>
);
